import * as z from "zod";
import { nanoid } from "nanoid";
import { zql } from "../generatedDrizzleSchema";
import { mutator } from "./mutator";

const updateUsagePriceSchema = z.object({
  id: z.string().optional(),
  spaceId: z.string().optional(),
  connectorId: z.string().optional(),
  measure: z.string().min(1),
  dimension: z.string().optional(),
  amountPerUnit: z.number().positive(),
});

export const updateUsagePrice = mutator({
  schema: updateUsagePriceSchema,
  resolve: {},
  permissions: [
    {
      permission: "tenant:ai_providers_manage",
      scopeId: ({ ctx }) => ctx.tenantId,
    },
  ],
  fn: async ({ tx, ctx, args }) => {
    const now = Date.now();
    const spaceId = args.spaceId ?? null;
    const connectorId = args.connectorId ?? null;
    const dimension = args.dimension ?? "";

    // Resolve by the exact dimension slice, never a caller-supplied id which
    // could name a price in another scope; the (dimensions, measure, dimension)
    // tuple is the natural key enforced by the NULLS NOT DISTINCT unique index.
    // The tenantId filter keeps the unscoped lookup (no RLS) from updating
    // another tenant's price row for a foreign spaceId/connectorId.
    let scoped = zql.connectorUsagePrices
      .where("tenantId", ctx.tenantId)
      .where("measure", args.measure)
      .where("dimension", dimension);
    scoped =
      spaceId === null
        ? scoped.where("spaceId", "IS", null)
        : scoped.where("spaceId", spaceId);
    scoped =
      connectorId === null
        ? scoped.where("connectorId", "IS", null)
        : scoped.where("connectorId", connectorId);
    const existing = await tx.run(scoped.one());

    const values = {
      spaceId,
      connectorId,
      measure: args.measure,
      dimension,
      amountPerUnit: args.amountPerUnit,
      updatedAt: now,
    };
    if (existing) {
      await tx.mutate.connectorUsagePrices.update({
        id: existing.id,
        ...values,
      });
      return;
    }
    await tx.mutate.connectorUsagePrices.insert({
      id: args.id ?? nanoid(12),
      ...values,
      createdAt: now,
      tenantId: ctx.tenantId,
    });
  },
});
